import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CreateBoatDto, UpdateBoatDto, BoatImage, FishingBoat } from '../types';
import api, { API_BASE_URL } from '../services/api';

const BoatForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  // 이전 페이지의 상태를 저장
  const previousLocation = location.state?.from || '/boats';
  const listData = location.state?.listData;
  const scrollPosition = location.state?.scrollPosition;

  const [formData, setFormData] = useState<CreateBoatDto>({
    name: '',
    region: '',
    homePort: '',
    ownerName: '',
    phones: [],
    kakaoId: '',
    kakaoUrl: '',
    naverBlogId: '',
    naverBlogUrl: '',
    instagramId: '',
    instagramUrl: '',
    websiteUrl: '',
    thefishingUrl: '',
    boatType: '',
    maxPassengers: undefined,
    facilities: [],
    targetSpecies: [],
    description: '',
    operatingHours: '',
    latitude: undefined,
    longitude: undefined,
    address: '',
    isActive: true,
    isVerified: false,
  });


  const [facilitiesInput, setFacilitiesInput] = useState('');
  const [targetSpeciesInput, setTargetSpeciesInput] = useState('');
  const [phonesInput, setPhonesInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [images, setImages] = useState<BoatImage[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [duplicateBoats, setDuplicateBoats] = useState<FishingBoat[]>([]);
  const [duplicateNameBoats, setDuplicateNameBoats] = useState<FishingBoat[]>([]);
  const [isCheckingName, setIsCheckingName] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchBoat();
    }
  }, [id]);

  // 페이지 진입 시 배 이름 중복 체크
  useEffect(() => {
    if (formData.name && formData.name.trim()) {
      checkDuplicateName(formData.name.trim());
    }
  }, [formData.name]);

  const checkDuplicateName = async (name: string) => {
    if (!name) {
      setDuplicateNameBoats([]);
      return;
    }

    try {
      setIsCheckingName(true);
      const duplicates = await api.checkDuplicateName(name, isEdit ? Number(id) : undefined);
      setDuplicateNameBoats(duplicates);
    } catch (err) {
      console.error('Failed to check duplicate name:', err);
    } finally {
      setIsCheckingName(false);
    }
  };

  const fetchBoat = async () => {
    try {
      setIsFetching(true);
      const boat = await api.getBoat(Number(id));
      setFormData({
        name: boat.name,
        region: boat.region,
        homePort: boat.homePort || '',
        ownerName: boat.ownerName || '',
        phones: boat.phones || [],
        kakaoId: boat.kakaoId || '',
        kakaoUrl: boat.kakaoUrl || '',
        naverBlogId: boat.naverBlogId || '',
        naverBlogUrl: boat.naverBlogUrl || '',
        instagramId: boat.instagramId || '',
        instagramUrl: boat.instagramUrl || '',
        websiteUrl: boat.websiteUrl || '',
        thefishingUrl: boat.thefishingUrl || '',
        boatType: boat.boatType || '',
        maxPassengers: boat.maxPassengers,
        facilities: boat.facilities || [],
        targetSpecies: boat.targetSpecies || [],
        description: boat.description || '',
        operatingHours: boat.operatingHours || '',
        latitude: boat.latitude,
        longitude: boat.longitude,
        address: boat.address || '',
        isActive: boat.isActive,
        isVerified: boat.isVerified,
      });
      setFacilitiesInput((boat.facilities || []).join(', '));
      setTargetSpeciesInput((boat.targetSpecies || []).join(', '));
      setPhonesInput((boat.phones || []).join(', '));
      setImages(boat.images || []);
    } catch (err: any) {
      console.error('Failed to fetch boat:', err);
      setError(err.response?.data?.message || '낚싯배 정보를 불러오는데 실패했습니다.');
    } finally {
      setIsFetching(false);
    }
  };

  const handleChange = async (field: keyof CreateBoatDto, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // 웹사이트 URL 변경 시 중복 체크
    if (field === 'websiteUrl' && value && value.trim()) {
      try {
        const duplicates = await api.checkDuplicateWebsite(value.trim(), isEdit ? Number(id) : undefined);
        setDuplicateBoats(duplicates);
      } catch (err) {
        console.error('Failed to check duplicate:', err);
      }
    } else if (field === 'websiteUrl') {
      setDuplicateBoats([]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      // 파일을 lastModified (다운받은 시간) 순서로 정렬
      const filesArray = Array.from(e.target.files);
      const sortedFiles = filesArray.sort((a, b) => a.lastModified - b.lastModified);
      setSelectedFiles(sortedFiles);
    }
  };

  const handleUploadImages = async () => {
    if (!isEdit || !id || selectedFiles.length === 0) return;

    try {
      setIsUploadingImages(true);
      const uploadedImages = await api.uploadImages(Number(id), selectedFiles);
      setImages([...images, ...uploadedImages]);
      setSelectedFiles([]);
      setSuccess('이미지가 업로드되었습니다.');
    } catch (err: any) {
      console.error('Failed to upload images:', err);
      setError(err.response?.data?.message || '이미지 업로드에 실패했습니다.');
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!isEdit || !id) return;

    try {
      await api.deleteImage(Number(id), imageId);
      setImages(images.filter((img) => img.id !== imageId));
      setSuccess('이미지가 삭제되었습니다.');
    } catch (err: any) {
      console.error('Failed to delete image:', err);
      setError(err.response?.data?.message || '이미지 삭제에 실패했습니다.');
    }
  };

  const handleRemoveSelectedFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleDelete = async () => {
    if (!isEdit || !id) return;

    setIsLoading(true);
    try {
      await api.deleteBoat(Number(id));
      // 삭제 후 리스트로 돌아감 (데이터 새로고침 필요하므로 state 전달 안함)
      navigate(previousLocation, { replace: true });
    } catch (err: any) {
      console.error('Failed to delete boat:', err);
      setError(err.response?.data?.message || '낚싯배 삭제에 실패했습니다.');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name || !formData.region) {
      setError('배 이름과 지역은 필수 입력 항목입니다.');
      return;
    }

    const submitData = {
      ...formData,
      // 전화번호 배열 처리: 쉼표로 분리하고 하이픈(-), 마침표(.) 제거
      phones: phonesInput
        ? phonesInput.split(',').map((p) => p.trim().replace(/[-\.]/g, '')).filter(Boolean)
        : [],
      facilities: facilitiesInput
        ? facilitiesInput.split(',').map((f) => f.trim()).filter(Boolean)
        : [],
      targetSpecies: targetSpeciesInput
        ? targetSpeciesInput.split(',').map((f) => f.trim()).filter(Boolean)
        : [],
      // URL 필드는 빈 문자열이면 undefined로 변환 (백엔드 @IsUrl() 검증 통과)
      websiteUrl: formData.websiteUrl || undefined,
      naverCafeUrl: formData.naverCafeUrl || undefined,
      blogUrl: formData.blogUrl || undefined,
      facebookUrl: formData.facebookUrl || undefined,
      thumbnailUrl: formData.thumbnailUrl || undefined,
      coverImageUrl: formData.coverImageUrl || undefined,
      sourceUrl: formData.sourceUrl || undefined,
      thefishingUrl: formData.thefishingUrl || undefined,
    };

    try {
      setIsLoading(true);
      if (isEdit) {
        await api.updateBoat(Number(id), submitData as UpdateBoatDto);
        setSuccess('낚싯배 정보가 수정되었습니다.');
      } else {
        const newBoat = await api.createBoat(submitData as CreateBoatDto);

        // 선택된 이미지가 있으면 자동으로 업로드
        if (selectedFiles.length > 0) {
          try {
            setSuccess('낚싯배가 추가되었습니다. 이미지를 업로드 중입니다...');
            await api.uploadImages(newBoat.id, selectedFiles);
            setSuccess('낚싯배와 이미지가 추가되었습니다.');
          } catch (uploadErr: any) {
            console.error('Failed to upload images:', uploadErr);
            setSuccess('낚싯배는 추가되었지만 일부 이미지 업로드에 실패했습니다.');
          }
        } else {
          setSuccess('낚싯배가 추가되었습니다.');
        }

        // 편집 페이지로 이동
        setTimeout(() => navigate(`/boats/edit/${newBoat.id}`, {
          state: { from: previousLocation }
        }), 1500);
        return;
      }
      // 수정 완료 후 이전 페이지로 (스크롤 위치만 유지, 데이터는 새로고침)
      setTimeout(() => navigate(previousLocation, {
        replace: true,
        state: {
          scrollPosition: scrollPosition
        }
      }), 1500);
    } catch (err: any) {
      console.error('Failed to save boat:', err);
      setError(err.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
        {isEdit ? '낚싯배 수정' : '낚싯배 추가'}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        {isEdit ? '낚싯배 정보를 수정합니다' : '새로운 낚싯배를 추가합니다'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            기본 정보
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  required
                  fullWidth
                  label="배 이름"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                />
                <Button
                  variant="outlined"
                  onClick={() => checkDuplicateName(formData.name)}
                  disabled={!formData.name || isCheckingName}
                  startIcon={isCheckingName ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                  sx={{ minWidth: '100px', height: '56px' }}
                >
                  {isCheckingName ? '확인중' : '중복확인'}
                </Button>
              </Box>
              {duplicateNameBoats.length > 0 && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    ⚠️ 같은 이름의 배 {duplicateNameBoats.length}척 발견
                  </Typography>
                  {duplicateNameBoats.map((boat) => (
                    <Typography key={boat.id} variant="body2" sx={{ mt: 0.5 }}>
                      • [{boat.id}] {boat.name} ({boat.region})
                      {boat.isVerified && <Chip label="검증" size="small" color="warning" sx={{ ml: 1 }} />}
                      {boat.isActive && <Chip label="활성" size="small" color="success" sx={{ ml: 1 }} />}
                    </Typography>
                  ))}
                </Alert>
              )}
              {formData.name && formData.name.trim() && duplicateNameBoats.length === 0 && !isCheckingName && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    ✓ 사용 가능한 배 이름입니다
                  </Typography>
                </Alert>
              )}
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                required
                fullWidth
                label="지역"
                value={formData.region}
                onChange={(e) => handleChange('region', e.target.value)}
                placeholder="예: 서해권 > 충청남도 > 서천군 > 홍원항"
                helperText="TheFishing 형식으로 입력 (지역 > 시/도 > 시/군/구 > 항구)"
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                label="모항"
                value={formData.homePort}
                onChange={(e) => handleChange('homePort', e.target.value)}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                label="주소"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="예: 충남 보령시 웅천읍"
                helperText="낚시배 출항 위치의 상세 주소"
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                label="연락처"
                value={phonesInput}
                onChange={(e) => setPhonesInput(e.target.value)}
                placeholder="쉼표(,)로 구분: 010-1234-5678, 02-123-4567"
                helperText="여러 번호는 쉼표로 구분, 하이픈(-) 및 마침표(.)는 자동 제거됨"
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                type="number"
                label="인승 (최대 승객)"
                value={formData.maxPassengers || ''}
                onChange={(e) =>
                  handleChange('maxPassengers', e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="예: 22"
                helperText="숫자만 입력 (예: 22인승 → 22)"
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="웹사이트 URL (홈페이지)"
                  value={formData.websiteUrl}
                  onChange={(e) => handleChange('websiteUrl', e.target.value)}
                  placeholder="예: https://example.com"
                />
                {formData.websiteUrl && (
                  <IconButton
                    color="primary"
                    href={formData.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mt: 1 }}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                )}
              </Box>
              {duplicateBoats.length > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    ⚠️ 같은 웹사이트를 사용하는 배 {duplicateBoats.length}척 발견
                  </Typography>
                  {duplicateBoats.map((boat) => (
                    <Typography key={boat.id} variant="body2" sx={{ mt: 0.5 }}>
                      • [{boat.id}] {boat.name} ({boat.region})
                      {boat.isVerified && <Chip label="검증" size="small" color="warning" sx={{ ml: 1 }} />}
                      {boat.isActive && <Chip label="활성" size="small" color="success" sx={{ ml: 1 }} />}
                    </Typography>
                  ))}
                </Alert>
              )}
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                label="TheFishing URL (예약 페이지)"
                value={formData.thefishingUrl}
                onChange={(e) => handleChange('thefishingUrl', e.target.value)}
                placeholder="예: https://www.thefishing.co.kr/boat/..."
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            상세 정보
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                label="편의시설"
                value={facilitiesInput}
                onChange={(e) => setFacilitiesInput(e.target.value)}
                placeholder="쉼표(,)로 구분하여 입력: 화장실, 주방, 냉장고"
                helperText="쉼표(,)로 구분하여 입력하세요"
              />
            </Box>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                label="대상 어종"
                value={targetSpeciesInput}
                onChange={(e) => setTargetSpeciesInput(e.target.value)}
                placeholder="쉼표(,)로 구분하여 입력: 광어, 우럭, 농어, 쭈꾸미, 갑오징어"
                helperText="쉼표(,)로 구분하여 입력하세요"
              />
            </Box>
            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="소개"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="낚시배 소개 내용을 입력하세요"
                helperText="클라이언트 앱에 표시될 소개 내용입니다"
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                type="number"
                label="위도 (Latitude)"
                value={formData.latitude || ''}
                onChange={(e) =>
                  handleChange('latitude', e.target.value ? Number(e.target.value) : undefined)
                }
                inputProps={{ step: 'any' }}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '250px' }}>
              <TextField
                fullWidth
                type="number"
                label="경도 (Longitude)"
                value={formData.longitude || ''}
                onChange={(e) =>
                  handleChange('longitude', e.target.value ? Number(e.target.value) : undefined)
                }
                inputProps={{ step: 'any' }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              이미지
            </Typography>

            <Box sx={{ mb: 3 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                multiple
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="image-upload">
                <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />}>
                  이미지 선택
                </Button>
              </label>
              {selectedFiles.length > 0 && (
                <>
                  <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2">
                      {selectedFiles.length}개 파일 선택됨 (다운받은 시간 순서로 정렬됨)
                    </Typography>
                    {isEdit && (
                      <Button
                        variant="contained"
                        onClick={handleUploadImages}
                        disabled={isUploadingImages}
                        startIcon={<CloudUploadIcon />}
                      >
                        {isUploadingImages ? '업로드 중...' : '업로드'}
                      </Button>
                    )}
                  </Box>

                  {/* 미리보기 */}
                  <ImageList cols={4} gap={16} sx={{ mt: 2 }}>
                    {selectedFiles.map((file, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`미리보기 ${index + 1}`}
                          loading="lazy"
                          style={{ height: 200, objectFit: 'cover' }}
                        />
                        <ImageListItemBar
                          title={`${index + 1}. ${file.name}`}
                          subtitle={`업로드 순서: ${index + 1}번째`}
                          actionIcon={
                            <IconButton
                              sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                              onClick={() => handleRemoveSelectedFile(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </>
              )}
            </Box>

            {isEdit && images.length > 0 && (
              <ImageList cols={4} gap={16} sx={{ mb: 3 }}>
                {images.map((image) => (
                  <ImageListItem key={image.id}>
                    <img
                      src={`${API_BASE_URL}/uploads/boats/${id}/${image.filename}`}
                      alt={`Boat ${image.orderIndex}`}
                      loading="lazy"
                      style={{ height: 200, objectFit: 'cover' }}
                    />
                    <ImageListItemBar
                      title={`이미지 ${image.orderIndex + 1}`}
                      actionIcon={
                        <IconButton
                          sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                          onClick={() => handleDeleteImage(image.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}

            {!isEdit && selectedFiles.length > 0 && (
              <Alert severity="info" sx={{ mb: 3 }}>
                선택한 이미지는 낚싯배 저장 시 자동으로 업로드됩니다.
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />
          </>

          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            상태
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                }
                label="활성 상태"
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isVerified}
                    onChange={(e) => handleChange('isVerified', e.target.checked)}
                  />
                }
                label="검증 완료"
              />
            </Box>
          </Box>

          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            {isEdit && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={isLoading}
              >
                삭제
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => {
                navigate(previousLocation, {
                  replace: true,
                  state: {
                    returnedData: listData,
                    scrollPosition: scrollPosition
                  }
                });
              }}
              disabled={isLoading}
            >
              취소
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={isLoading}
            >
              {isLoading ? '저장 중...' : isEdit ? '수정' : '추가'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default BoatForm;
